import React, { useState } from "react";
import { FiX, FiCopy, FiCheck, FiExternalLink, FiCode, FiCpu, FiClock } from "react-icons/fi";
import toast from "react-hot-toast";

const DSASolutionModal = ({ question, onClose }) => {
  const [selectedLang, setSelectedLang] = useState("cpp");
  const [copied, setCopied] = useState(false);

  if (!question) return null;

  const solutions = question.solutions || [];
  const currentSolution = solutions.find((s) => s.language === selectedLang) || solutions[0] || {
    code: "// Solution coming soon...",
    explanation: "Explanation pending."
  };

  const handleCopy = () => {
    if (currentSolution?.code) {
      navigator.clipboard.writeText(currentSolution.code);
      setCopied(true);
      toast.success("Code copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getLangLabel = (lang) => {
    switch (lang) {
      case "cpp": return "C++";
      case "java": return "Java";
      case "python": return "Python 3";
      case "javascript": return "JavaScript";
      default: return lang.toUpperCase();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden text-slate-900 dark:text-slate-100 transition-colors">
        
        {/* Modal Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900/90">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <FiCode className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">{question.title}</h2>
                <span
                  className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                    question.difficulty === "Easy"
                      ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20"
                      : question.difficulty === "Medium"
                      ? "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20"
                      : "bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20"
                  }`}
                >
                  {question.difficulty}
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{question.dayTopic} • {question.category}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <a
              href={question.leetcodeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-1.5 text-xs font-medium bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30 px-3 py-1.5 rounded-lg transition-all"
            >
              <span>LeetCode</span>
              <FiExternalLink className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white flex items-center justify-center transition-all cursor-pointer"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 flex-1 overflow-y-auto space-y-5 custom-scrollbar">
          {/* Language Selector Bar */}
          <div className="flex items-center justify-between bg-slate-100 dark:bg-slate-950 p-1.5 rounded-xl border border-slate-200 dark:border-slate-800">
            <div className="flex items-center space-x-1 overflow-x-auto custom-scrollbar">
              {["cpp", "java", "python", "javascript"].map((lang) => {
                const isAvailable = solutions.some((s) => s.language === lang);
                const isActive = selectedLang === lang;
                return (
                  <button
                    key={lang}
                    onClick={() => setSelectedLang(lang)}
                    className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                      isActive
                        ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/30"
                        : isAvailable
                        ? "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-800/60"
                        : "text-slate-400 dark:text-slate-600 opacity-60 cursor-not-allowed"
                    }`}
                  >
                    {getLangLabel(lang)}
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleCopy}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-xs font-medium transition-all cursor-pointer border border-slate-200 dark:border-transparent ml-2"
            >
              {copied ? <FiCheck className="w-4 h-4 text-emerald-500 dark:text-emerald-400" /> : <FiCopy className="w-4 h-4" />}
              <span>{copied ? "Copied!" : "Copy Code"}</span>
            </button>
          </div>

          {/* Code Viewer Box */}
          <div className="relative bg-slate-950 border border-slate-800/80 rounded-xl overflow-hidden shadow-inner">
            <div className="bg-slate-900/80 px-4 py-2 border-b border-slate-800 flex items-center justify-between text-xs text-slate-400 font-mono">
              <span>solution.{selectedLang === "cpp" ? "cpp" : selectedLang === "java" ? "java" : selectedLang === "python" ? "py" : "js"}</span>
              <span className="text-slate-500">Interactive Solution</span>
            </div>
            <pre className="p-4 text-xs md:text-sm font-mono text-indigo-200 overflow-x-auto leading-relaxed custom-scrollbar max-h-96">
              <code>{currentSolution.code}</code>
            </pre>
          </div>

          {/* Solution Complexity & Explanation Card */}
          {currentSolution.explanation && (
            <div className="bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 rounded-xl p-4 space-y-2">
              <div className="flex items-center space-x-2 text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                <FiCpu className="w-4 h-4" />
                <span>Approach & Complexity Analysis</span>
              </div>
              <p className="text-xs md:text-sm text-slate-700 dark:text-slate-300 leading-relaxed font-sans">
                {currentSolution.explanation}
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/90 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
          <div className="flex items-center space-x-2">
            <FiClock className="w-4 h-4 text-slate-400 dark:text-slate-500" />
            <span>Estimated time: {question.estimatedTime || "30Min"}</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-900 dark:text-white font-medium transition-all cursor-pointer"
          >
            Close Window
          </button>
        </div>

      </div>
    </div>
  );
};

export default DSASolutionModal;
